/* eslint-disable max-lines-per-function, complexity */
import {
  SSRModalDropDownSelection,
  type SSRModalDropDownSelectionProps,
} from '@fz/ui-components-react';
import { PassengerTypeCode, SsrType } from '@models/common/shared-enums.model';
import type { ExtrasResponse, Passenger } from '@models/api/extras-response.model';
import type { FlightTabItem } from '@models/landing/api/baggage.model';
import type { CodeTypeList, MealsList } from '@models/master-data/master-data.model';
import './_meals-ssr-modal.scss';
import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { IconClose } from '@shared/icons';
import {
  mapMealsQuotesToModalView,
  type MealsAddApiPayload,
} from '@shared/components/MealsSsrModalComponent/meals.view.mapper';
import {
  findNextSelectablePassengerId,
  isInfantPassengerTypeLabel,
  resolvePassengerTypeLabel,
} from '@shared/components/ssr-passenger.util';
import { getResource } from '@utils/resource-translator.util';
import { PASSENGER_NAVIGATION_DELAY } from '@utils/passenger-navigation.util';
import { selectPreferredServices, useMasterDataStore } from '@store/master-data.store';

// ============================================================================
// TYPES
// ============================================================================

interface MealsModalProps {
  mealQuotes?: ExtrasResponse['mealQuotes'];
  flightTabItems?: FlightTabItem[];
  codeTypeList?: CodeTypeList;
  mealsList?: MealsList;
  passengerList?: Passenger[];
  selectedSSRList?: ExtrasResponse['selectedSSRList'];
  onConfirmSelection?: (payloads: MealsAddApiPayload[]) => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

/** keyed by logical+physical flight key → resPaxId string → selected meal codeType */
type MealsTabPassengerSelectionMap = Record<string, Record<string, string>>;
type MealsOption = ReturnType<
  typeof mapMealsQuotesToModalView
>['flightTabs'][number]['mealOptions'][number];

interface SelectedMealSsrListEntry {
  ssrType?: unknown;
  codeType?: unknown;
  resPaxId?: unknown;
  resPaxID?: unknown;
  logicalFlightId?: unknown;
  logicalFlightID?: unknown;
  physicalFlightId?: unknown;
  physicalFlightID?: unknown;
}

interface NormalizedSelectedMealEntry {
  codeType: string;
  logicalFlightId: string;
  physicalFlightId: string;
  resPaxId: number;
}

const MEALS_SSR_TYPE = String(SsrType.MEAL);
const INFANT_PASSENGER_TYPE = Number(PassengerTypeCode.Infant);

const normalizePassengerType = (passengerType: Passenger['passengerType']): number => {
  return Number(passengerType);
};

const isInfantPassenger = (passenger: Passenger): boolean => {
  return normalizePassengerType(passenger.passengerType) === INFANT_PASSENGER_TYPE;
};

const resolveDefaultSelectablePassengerId = (
  passengers: Passenger[],
  fallbackId: string,
): string => {
  const firstNonInfant = passengers.find((passenger) => !isInfantPassenger(passenger));

  return firstNonInfant ? firstNonInfant.resPaxId.toString() : fallbackId;
};

const ALL_PASSENGERS_ID = 'all-passengers';
const INFANT_MEALS_ELIGIBLE_RESOURCE_KEY = 'SelectPassenger.NotApplMeal.Tooltip.Description.Text';
const SSR_CLOSE_ICON = <IconClose size={27} aria-hidden={true} />;

const resolveResourceText = (key: string, fallback: string = key): string => {
  const translatedValue = getResource(key);

  if (translatedValue === '' || translatedValue === 'undefined') {
    return fallback;
  }

  return translatedValue;
};

const buildMealsTabKey = (logicalFlightId: string, physicalFlightId?: string): string => {
  return `${logicalFlightId}::${physicalFlightId ?? ''}`;
};

const findMealTabByKey = (
  mealTabs: ReturnType<typeof mapMealsQuotesToModalView>['flightTabs'],
  tabKey: string,
): ReturnType<typeof mapMealsQuotesToModalView>['flightTabs'][number] | undefined => {
  return mealTabs.find(
    (tab) => buildMealsTabKey(tab.logicalFlightId, tab.physicalFlightId) === tabKey,
  );
};

const isSelectedMealSsrListEntry = (value: unknown): value is SelectedMealSsrListEntry => {
  return typeof value === 'object' && value !== null;
};

const resolveStringField = (value: unknown): string => {
  return typeof value === 'string' ? value : '';
};

const resolveNumberField = (value: unknown): number => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }

  return Number.NaN;
};

const normalizeSelectedMealEntry = (item: unknown): NormalizedSelectedMealEntry | undefined => {
  if (!isSelectedMealSsrListEntry(item)) {
    return undefined;
  }

  const ssrType = resolveStringField(item.ssrType).trim().toUpperCase();
  const codeType = resolveStringField(item.codeType);
  const logicalFlightId =
    resolveStringField(item.logicalFlightId) || resolveStringField(item.logicalFlightID);
  const physicalFlightId =
    resolveStringField(item.physicalFlightId) || resolveStringField(item.physicalFlightID);
  const resPaxId = resolveNumberField(item.resPaxId ?? item.resPaxID);

  if (ssrType !== MEALS_SSR_TYPE || !codeType || !logicalFlightId || !Number.isFinite(resPaxId)) {
    return undefined;
  }

  return {
    codeType,
    logicalFlightId,
    physicalFlightId,
    resPaxId,
  };
};

const applySelectedMealsToSelectionMap = (
  baseSelectionMap: MealsTabPassengerSelectionMap,
  mealQuotes: ExtrasResponse['mealQuotes'] | undefined,
  selectedSSRList: ExtrasResponse['selectedSSRList'] | undefined,
): MealsTabPassengerSelectionMap => {
  if (!mealQuotes || !selectedSSRList || selectedSSRList.length === 0) {
    return baseSelectionMap;
  }

  const nextSelectionMap: MealsTabPassengerSelectionMap = Object.entries(
    baseSelectionMap,
  ).reduce<MealsTabPassengerSelectionMap>((acc, [tabKey, tabSelections]) => {
    acc[tabKey] = { ...tabSelections };
    return acc;
  }, {});

  selectedSSRList.forEach((item) => {
    const normalizedEntry = normalizeSelectedMealEntry(item);

    if (!normalizedEntry) {
      return;
    }

    const matchingQuote = mealQuotes.find((quote) => {
      const sameLogicalFlight = quote.logicalFlightId === normalizedEntry.logicalFlightId;
      const samePhysicalFlight =
        normalizedEntry.physicalFlightId === '' ||
        quote.physicalFlightId === normalizedEntry.physicalFlightId;

      return sameLogicalFlight && samePhysicalFlight;
    });

    if (!matchingQuote) {
      return;
    }

    const tabKey = buildMealsTabKey(matchingQuote.logicalFlightId, matchingQuote.physicalFlightId);
    const passengerId = normalizedEntry.resPaxId.toString();

    if (nextSelectionMap[tabKey]?.[passengerId] === undefined) {
      return;
    }

    nextSelectionMap[tabKey][passengerId] = normalizedEntry.codeType;
  });

  return nextSelectionMap;
};

// ============================================================================
// SELECTION MAP HELPERS
// ============================================================================

const buildSelectionMap = (
  mealQuotes?: ExtrasResponse['mealQuotes'],
  passengerList?: Passenger[],
  selectedSSRList?: ExtrasResponse['selectedSSRList'],
): MealsTabPassengerSelectionMap => {
  if (!mealQuotes) return {};

  const baseSelectionMap = mealQuotes.reduce<MealsTabPassengerSelectionMap>((acc, quote) => {
    const tabKey = buildMealsTabKey(quote.logicalFlightId, quote.physicalFlightId);
    acc[tabKey] = {};

    if (!passengerList) return acc;

    passengerList.forEach((passenger) => {
      const flightDetail = passenger.flightsDetail?.find(
        (fd) =>
          fd.logicalFlightId === quote.logicalFlightId &&
          (quote.physicalFlightId ? fd.physicalFlightId === quote.physicalFlightId : true),
      );
      const includedMeal = flightDetail?.includedExtrasList?.find(
        (extra) => String(extra.ssrType).toUpperCase() === MEALS_SSR_TYPE,
      );

      acc[tabKey][passenger.resPaxId.toString()] = includedMeal?.codeType ?? '';
    });

    return acc;
  }, {});

  return applySelectedMealsToSelectionMap(baseSelectionMap, mealQuotes, selectedSSRList);
};

// ============================================================================
// PAYLOAD BUILDER
// ============================================================================

const buildPayloadsForConfirm = (
  selectionByTabPassenger: MealsTabPassengerSelectionMap,
  mealQuotesData: ReturnType<typeof mapMealsQuotesToModalView>,
): MealsAddApiPayload[] => {
  return Object.entries(selectionByTabPassenger).flatMap(([tabKey, selectionByPassenger]) => {
    const mealTab = findMealTabByKey(mealQuotesData.flightTabs, tabKey);
    const logicalFlightId = mealTab?.logicalFlightId ?? '';
    const physicalFlightId = mealTab?.physicalFlightId;

    return Object.entries(selectionByPassenger).map(([resPaxIdStr, selectedCodeType]) => {
      const resPaxId = Number(resPaxIdStr);
      const selectedMeal =
        mealTab?.mealOptions.find((meal) => meal.codeType === selectedCodeType) ?? null;
      const selectedApplicablePax = selectedMeal?.applicablePax?.find(
        (applicablePax) => applicablePax.resPaxId === resPaxId,
      );
      const selectedPassenger = mealQuotesData.passengerList.find(
        (passenger) => passenger.resPaxId === resPaxId,
      );
      const selectedFlightDetail = selectedPassenger?.flightsDetail.find(
        (flightDetail) =>
          flightDetail.logicalFlightId === logicalFlightId &&
          (physicalFlightId ? flightDetail.physicalFlightId === physicalFlightId : true),
      );

      return {
        ssrType: mealTab?.ssrType ?? MEALS_SSR_TYPE,
        logicalFlightId:
          logicalFlightId !== '' ? logicalFlightId : (selectedFlightDetail?.logicalFlightId ?? ''),
        physicalFlightId: mealTab?.physicalFlightId ?? selectedFlightDetail?.physicalFlightId ?? '',
        amount: selectedMeal?.amountRaw ?? '',
        paxJourneyId:
          selectedApplicablePax?.paxJourneyId ??
          selectedFlightDetail?.paxJourneyId ??
          selectedPassenger?.paxJourneyId ??
          '',
        resPaxId,
        recordNumber: selectedApplicablePax?.recordNumber ?? selectedPassenger?.recordNumber ?? 0,
        quantityAvailable: selectedMeal?.quantityAvailable ?? 0,
        chargeComment: selectedMeal?.chargeComment ?? '',
        codeType: selectedMeal?.codeType ?? '',
        isBusiness: selectedMeal?.isBusiness ?? false,
        secureHash: selectedMeal?.secureHash ?? '',
      };
    });
  });
};

const resolvePassengerSsrDetails = (
  passengers: Passenger[],
  selectionByPassenger: Record<string, string>,
  mealOptions: ReturnType<typeof mapMealsQuotesToModalView>['flightTabs'][number]['mealOptions'],
  translate: (key: string, defaultValue?: string) => string,
): NonNullable<SSRModalDropDownSelectionProps['passengerSSRDetails']> => {
  return Object.entries(selectionByPassenger).reduce<
    NonNullable<SSRModalDropDownSelectionProps['passengerSSRDetails']>
  >((acc, [passengerId, selectedCodeType]) => {
    const selectedOption = mealOptions.find((option) => option.codeType === selectedCodeType);
    const passenger = passengers.find((item) => item.resPaxId.toString() === passengerId);
    const subText1 =
      passenger && isInfantPassenger(passenger)
        ? translate(INFANT_MEALS_ELIGIBLE_RESOURCE_KEY, INFANT_MEALS_ELIGIBLE_RESOURCE_KEY)
        : '';

    acc[passengerId] = {
      subText1,
      subText2: selectedOption?.codeName ?? '',
    };

    return acc;
  }, {});
};

// ============================================================================
// COMPONENT
// ============================================================================

export const MealsSsrComponent = ({
  mealQuotes,
  flightTabItems,
  codeTypeList,
  mealsList,
  passengerList,
  selectedSSRList,
  onConfirmSelection,
  isOpen,
  setIsOpen,
}: MealsModalProps): ReactElement => {
  const { t } = useTranslation('common');
  const preferredServices = useMasterDataStore(selectPreferredServices);

  const mealsViewData = useMemo(
    () =>
      mealQuotes && passengerList
        ? mapMealsQuotesToModalView(
            mealQuotes,
            passengerList,
            flightTabItems,
            codeTypeList,
            mealsList,
            preferredServices,
          )
        : null,
    [mealQuotes, passengerList, flightTabItems, codeTypeList, mealsList, preferredServices],
  );

  const initialFlightTabValue = mealsViewData?.flightTabs[0]
    ? buildMealsTabKey(
        mealsViewData.flightTabs[0].logicalFlightId,
        mealsViewData.flightTabs[0].physicalFlightId,
      )
    : '';
  const initialSelectedPassengerId = mealsViewData
    ? resolveDefaultSelectablePassengerId(
        mealsViewData.passengerList,
        mealsViewData.passengerList[0]?.resPaxId.toString() ?? '',
      )
    : '';

  const [selectedPassenger, setSelectedPassenger] = useState(initialSelectedPassengerId);
  const [activeFlightTab, setActiveFlightTab] = useState(initialFlightTabValue);
  const [defaultSelectionByTabPassenger, setDefaultSelectionByTabPassenger] =
    useState<MealsTabPassengerSelectionMap>(() =>
      buildSelectionMap(mealQuotes, passengerList, selectedSSRList),
    );
  const [selectionByTabPassenger, setSelectionByTabPassenger] =
    useState<MealsTabPassengerSelectionMap>(() =>
      buildSelectionMap(mealQuotes, passengerList, selectedSSRList),
    );
  const [confirmedSelectionByTabPassenger, setConfirmedSelectionByTabPassenger] =
    useState<MealsTabPassengerSelectionMap>(() =>
      buildSelectionMap(mealQuotes, passengerList, selectedSSRList),
    );
  const [isSameForAll, setIsSameForAll] = useState(false);

  useEffect(() => {
    const initialSelectionMap = buildSelectionMap(mealQuotes, passengerList, selectedSSRList);

    setSelectedPassenger(initialSelectedPassengerId);
    setActiveFlightTab(initialFlightTabValue);
    setDefaultSelectionByTabPassenger(initialSelectionMap);
    setSelectionByTabPassenger(initialSelectionMap);
    setConfirmedSelectionByTabPassenger(initialSelectionMap);
  }, [
    mealQuotes,
    passengerList,
    selectedSSRList,
    initialSelectedPassengerId,
    initialFlightTabValue,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setIsSameForAll(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const footerLabelElement = document.querySelector<HTMLElement>(
      '.ssr-modal-action-popup .ssr-modal-footer__price-label',
    );

    if (!footerLabelElement) {
      return;
    }

    footerLabelElement.textContent = resolveResourceText('Extras.AmountDue.Text');
  }, [isOpen]);

  const passengersTitle = getResource('SelectPassenger.Passengers.Text');

  // Apply same meal to all non-infant passengers when "same for all" is selected
  useEffect(() => {
    if (!isSameForAll || !mealsViewData) {
      return;
    }

    const selectedMealCodeType =
      selectionByTabPassenger[activeFlightTab]?.[selectedPassenger] ?? '';

    if (!selectedMealCodeType) {
      return;
    }

    setSelectionByTabPassenger((previous) => {
      const activeTabSelections = previous[activeFlightTab] ?? {};
      const nextTabSelections = Object.keys(activeTabSelections).reduce<Record<string, string>>(
        (acc, passengerId) => {
          const passenger = mealsViewData.passengerList.find(
            (p) => p.resPaxId.toString() === passengerId,
          );
          const isInfant = passenger ? isInfantPassenger(passenger) : false;

          if (isInfant) {
            acc[passengerId] = activeTabSelections[passengerId] ?? '';
          } else {
            acc[passengerId] = selectedMealCodeType;
          }

          return acc;
        },
        {},
      );

      return {
        ...previous,
        [activeFlightTab]: nextTabSelections,
      };
    });
  }, [isSameForAll, selectedPassenger, activeFlightTab, mealsViewData, selectionByTabPassenger]);

  const handleMealSelection = (passengerId: string, meal: MealsOption | null): void => {
    const selectedCodeType = meal?.codeType ?? '';

    setSelectionByTabPassenger((previous) => {
      const currentSelections = previous[activeFlightTab] ?? {};

      if (isSameForAll) {
        const nextTabSelections = Object.keys(currentSelections).reduce<Record<string, string>>(
          (acc, currentPassengerId) => {
            const passenger = mealsViewData?.passengerList.find(
              (item) => item.resPaxId.toString() === currentPassengerId,
            );

            if (passenger && isInfantPassenger(passenger)) {
              acc[currentPassengerId] = currentSelections[currentPassengerId] ?? '';
              return acc;
            }

            acc[currentPassengerId] = selectedCodeType;
            return acc;
          },
          {},
        );

        return {
          ...previous,
          [activeFlightTab]: nextTabSelections,
        };
      }

      return {
        ...previous,
        [activeFlightTab]: {
          ...currentSelections,
          [passengerId]: selectedCodeType,
        },
      };
    });

    if (!selectedCodeType || isSameForAll || !mealsViewData) {
      return;
    }

    const passengersForAutoAdvance = mealsViewData.passengerList.map((passenger) => ({
      id: passenger.resPaxId.toString(),
      type: resolvePassengerTypeLabel(passenger.passengerType),
    }));

    const nextPassengerId = findNextSelectablePassengerId(passengersForAutoAdvance, passengerId);

    if (nextPassengerId) {
      setTimeout(() => {
        setSelectedPassenger(nextPassengerId);
      }, PASSENGER_NAVIGATION_DELAY);
    }
  };

  const handleFlightTabChange = (tabValue: string): void => {
    setActiveFlightTab(tabValue);
    setIsSameForAll(false);

    const firstNonInfantPassengerId = mealsViewData?.passengerList
      .find((passenger) => !isInfantPassenger(passenger))
      ?.resPaxId.toString();
    const firstPassengerId = Object.keys(selectionByTabPassenger[tabValue] ?? {})[0] ?? '';

    setSelectedPassenger(firstNonInfantPassengerId ?? firstPassengerId);
  };

  const handlePassengerTabChange = (passengerId: string): void => {
    setSelectedPassenger(passengerId);
  };

  const handleConfirm = (): void => {
    if (!mealsViewData || !onConfirmSelection) {
      return;
    }

    const payloads = buildPayloadsForConfirm(selectionByTabPassenger, mealsViewData);
    setConfirmedSelectionByTabPassenger(selectionByTabPassenger);
    onConfirmSelection(payloads);
    setIsOpen(false);
  };

  const resetTransientSelectionState = (): void => {
    setSelectionByTabPassenger(confirmedSelectionByTabPassenger);
    setIsSameForAll(false);
    setSelectedPassenger(initialSelectedPassengerId);
    setActiveFlightTab(initialFlightTabValue);
  };

  const handleClearSelections = (): void => {
    setSelectionByTabPassenger((previous) => {
      const activeTabSelections = previous[activeFlightTab] ?? {};
      const defaultTabSelections = defaultSelectionByTabPassenger[activeFlightTab] ?? {};
      const nextTabSelections = Object.keys(activeTabSelections).reduce<Record<string, string>>(
        (accumulator, passengerId) => {
          accumulator[passengerId] = defaultTabSelections[passengerId] ?? '';
          return accumulator;
        },
        {},
      );

      return {
        ...previous,
        [activeFlightTab]: nextTabSelections,
      };
    });
  };

  const handleClearClick = (): void => {
    handleClearSelections();
  };

  const handleClose = (): void => {
    resetTransientSelectionState();
    setIsOpen(false);
  };

  if (!mealsViewData) {
    return <></>;
  }

  const currentFlightTab = findMealTabByKey(mealsViewData.flightTabs, activeFlightTab);
  const isUnavailableForModal = currentFlightTab?.isUnavailable ?? false;
  let unavailableTitle: string | undefined;
  let unavailableDescription: string | undefined;

  if (currentFlightTab?.unavailableReason === 'interline') {
    unavailableTitle = resolveResourceText('Extras.InterlineMeal.Title.Text');
    unavailableDescription = resolveResourceText('Extras.InterlineMeal.Desc.Text');
  } else if (currentFlightTab?.unavailableReason === 'cut-off') {
    unavailableTitle = resolveResourceText('Extras.Meals.CutOff.Modal.Title.Text');
    unavailableDescription = resolveResourceText('Extras.Meals.CutOff.Description.Text');
  } else if (currentFlightTab?.unavailableReason === 'no-meals') {
    unavailableTitle = resolveResourceText('Extras.Meals.CutOff.Modal.Title.Text');
    unavailableDescription = resolveResourceText('Extras.Meals.CutOff.Description.Text');
  }

  const unavailableVariant = isUnavailableForModal ? ('panel' as const) : undefined;
  const currentMealSelection = selectionByTabPassenger[activeFlightTab]?.[selectedPassenger] ?? '';
  const selectedMealOption =
    currentFlightTab?.mealOptions.find((meal) => meal.codeType === currentMealSelection) ?? null;

  // Don't show price amount and currency if meal amount is 0 (included meals)
  // const _decimalPlaces = 2;
  const priceAmountForFooter =
    selectedMealOption && selectedMealOption.amount > 0 ? selectedMealOption.amount.toString() : '';
  const currencyCodeForFooter =
    selectedMealOption && selectedMealOption.amount > 0 ? (currentFlightTab?.currency ?? '') : '';
  const nonInfantPassengersCount = mealsViewData.passengerList.filter(
    (passenger) => !isInfantPassenger(passenger),
  ).length;
  const showSameForAllCheckbox = false;
  const passengersForModal: SSRModalDropDownSelectionProps['passengers'] =
    mealsViewData.passengerList.map((passenger) => ({
      id: passenger.resPaxId.toString(),
      name: [passenger.title, passenger.firstName, passenger.lastName].filter(Boolean).join(' '),
      type: resolvePassengerTypeLabel(passenger.passengerType),
      isInfant: isInfantPassenger(passenger),
      seatNumber: '',
    }));
  const allPassengersCard = [
    {
      id: ALL_PASSENGERS_ID,
      name: `${t('Extras.AllPassengers.Text', 'All passengers')} (${nonInfantPassengersCount})`,
      type: '',
      seatNumber: '',
    },
  ];
  const infantPassengers = passengersForModal.filter((passenger) =>
    isInfantPassengerTypeLabel(passenger.type),
  );
  const passengersForModalView = isSameForAll
    ? [...allPassengersCard, ...infantPassengers]
    : passengersForModal;
  const selectedPassengerId = isSameForAll
    ? ALL_PASSENGERS_ID
    : selectedPassenger || passengersForModal[0]?.id || '';

  const selectedOptionIdForGroup = isSameForAll
    ? Object.entries(selectionByTabPassenger[activeFlightTab] ?? {}).find(
        ([passengerId, optionId]) => {
          const passenger = mealsViewData.passengerList.find(
            (item) => item.resPaxId.toString() === passengerId,
          );

          return passenger ? !isInfantPassenger(passenger) && Boolean(optionId) : false;
        },
      )?.[1]
    : currentMealSelection || undefined;

  const groupsForModal: SSRModalDropDownSelectionProps['groups'] =
    currentFlightTab && !isUnavailableForModal
      ? [
          {
            id: 'meal-selection',
            selectedOptionId: selectedOptionIdForGroup,
            placeholder: getResource('SelectPassenger.ChooseMeal.Text'),
            changeLabel: getResource('Extras.ChangeLabel.Text'),
            triggerAriaLabel: t('Extras.SelectMeal.AriaLabel.Text'),
            dropdownName: 'meal',
            options: currentFlightTab.mealOptions.map((meal) => ({
              id: meal.codeType,
              label: meal.codeName,
              trailingText:
                meal.amount === 0
                  ? getResource('Review.IncludedExtras.Text')
                  : `${meal.amount} ${meal.currency}`,
              isMostPopular: meal.isMostPopular,
              mostPopularLabel: meal.mostPopularLabel,
            })),
            emptyOptionsText: t('Extras.SelectMeal.Empty.Text', 'No meals available'),
          },
        ]
      : [];

  const passengerSsrDetailsForModal = isSameForAll
    ? {
        [ALL_PASSENGERS_ID]: {
          subText1: '',
          subText2: selectedMealOption?.codeName ?? '',
        },
        ...Object.fromEntries(
          infantPassengers.map((passenger) => [
            passenger.id,
            {
              subText1: t(INFANT_MEALS_ELIGIBLE_RESOURCE_KEY, INFANT_MEALS_ELIGIBLE_RESOURCE_KEY),
              subText2: '',
            },
          ]),
        ),
      }
    : resolvePassengerSsrDetails(
        mealsViewData.passengerList,
        selectionByTabPassenger[activeFlightTab] ?? {},
        currentFlightTab?.mealOptions ?? [],
        resolveResourceText,
      );

  const hasActiveTabChanges = Object.keys(selectionByTabPassenger[activeFlightTab] ?? {}).some(
    (passengerId) => {
      return (
        (selectionByTabPassenger[activeFlightTab]?.[passengerId] ?? '') !==
        (defaultSelectionByTabPassenger[activeFlightTab]?.[passengerId] ?? '')
      );
    },
  );

  const hasLegLevelFlightTabs = mealsViewData.flightTabs.some(
    (tab, index, tabs) =>
      tabs.findIndex((item) => item.logicalFlightId === tab.logicalFlightId) !== index,
  );

  const flightTabsForModal: NonNullable<SSRModalDropDownSelectionProps['flightTabItems']> =
    mealsViewData.flightTabs.map((tab, index) => {
      const tabItemOverride = (flightTabItems ?? [])[index];

      if (hasLegLevelFlightTabs) {
        const segmentLabel = tabItemOverride?.label ?? (flightTabItems ?? [])[0]?.label;
        const segmentContent = tabItemOverride?.content ?? (flightTabItems ?? [])[0]?.content;

        if (segmentLabel && segmentContent) {
          return {
            value: buildMealsTabKey(tab.logicalFlightId, tab.physicalFlightId),
            label: segmentLabel,
            content: segmentContent,
          };
        }

        const legLabel = tab.flightNumber ? `Flight ${tab.flightNumber}` : `Leg ${index + 1}`;

        return {
          value: buildMealsTabKey(tab.logicalFlightId, tab.physicalFlightId),
          label: legLabel,
          content: legLabel,
        };
      }

      return {
        value: buildMealsTabKey(tab.logicalFlightId, tab.physicalFlightId),
        label:
          tabItemOverride?.label ??
          (index === 0
            ? t('Common.Departure.Text', 'Departure')
            : t('Common.Return.Text', 'Return')),
        content:
          tabItemOverride?.content ??
          (tab.flightNumber ? `Flight ${tab.flightNumber}` : tab.logicalFlightId),
      };
    });

  return (
    <SSRModalDropDownSelection
      isOpen={isOpen}
      onClose={handleClose}
      title={getResource('Extras.Meals.Text')}
      closeIcon={SSR_CLOSE_ICON}
      passengersTitle={passengersTitle}
      contentTitle={getResource('Extras.SelectMeal.ContentTitle.Text')}
      contentDescription={
        isUnavailableForModal ? '' : getResource('Extras.SelectMeal.Popup.Description.Text')
      }
      confirmButtonLabel={getResource('Confirm.Text')}
      passengers={passengersForModalView}
      selectedPassengerId={selectedPassengerId}
      onPassengerSelect={(passengerId) => {
        if (passengerId === ALL_PASSENGERS_ID) {
          return;
        }

        const isInfant =
          mealsViewData.passengerList.find((item) => item.resPaxId.toString() === passengerId)
            ?.passengerType === PassengerTypeCode.Infant;

        if (isInfant) {
          return;
        }

        handlePassengerTabChange(passengerId);
      }}
      passengerSSRDetails={passengerSsrDetailsForModal}
      flightTabItems={flightTabsForModal}
      activeFlightTabValue={activeFlightTab}
      onFlightTabChange={handleFlightTabChange}
      groups={groupsForModal}
      onSelectOption={(_, optionId) => {
        const option = currentFlightTab?.mealOptions.find((meal) => meal.codeType === optionId);
        const isSameOption = selectedOptionIdForGroup === optionId;

        handleMealSelection(selectedPassenger, option && !isSameOption ? option : null);
      }}
      passengerTypeLabelProps={{ className: 'label5', variant: 'tertiary' }}
      onConfirm={handleConfirm}
      onClear={handleClearClick}
      priceAmount={priceAmountForFooter}
      currencyCode={currencyCodeForFooter}
      disableInfants={true}
      showClearButton={hasActiveTabChanges}
      clearLabel={getResource('Clear.Text')}
      showSameForAllPassengers={showSameForAllCheckbox}
      isUnavailable={isUnavailableForModal}
      unavailableTitle={unavailableTitle}
      unavailableDescription={unavailableDescription}
      unavailableVariant={unavailableVariant}
      mostPopularBadgeLabel={getResource('ContactDetails.Popular.Text')}
    />
  );
};
